import { type RefObject, useLayoutEffect, useState } from "react";

import { scrollParentOf } from "@application/utilities/scroll_parent";

// Sizes an embedded panel to fill from its own top edge down to the bottom of
// the viewport, so it adapts to the REAL header height — even when a long
// breadcrumb or claimant name wraps the banner to two lines. This replaces a
// hardcoded `100vh - Npx` calc, which guesses a fixed header height and so
// overshoots (clipping the panel below the fold) the moment the header wraps.
//
// The panel's top — and the height it should fill to — moves whenever the
// viewport or anything above the panel changes, so we recompute on:
//   • viewport resize (the breadcrumb re-wraps, or the window settles after
//     load) — window `resize`;
//   • the panel's own width changing (the detail rail slides open) and a sibling
//     directly above it settling (the summary strip) — a ResizeObserver on the
//     panel and its parent;
//   • the header banner settling to a shorter height after first paint — the
//     banner is a sibling of an ancestor, so the only boxes that change are the
//     scroll region's own children, which we observe too.
// measure() is a single rect read + guarded setState, so it needs no throttle;
// ResizeObserver is already frame-throttled by the browser, and a 1px threshold
// stops the feedback loop our own height change would otherwise trigger.
export function useFillHeight<T extends HTMLElement>(
  ref: RefObject<T | null>,
  enabled: boolean,
  // Breathing room below the panel — matches the page's bottom padding (p-4).
  gap = 16,
): number | undefined {
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!enabled) return;
    const element = ref.current;
    if (!element) return;

    const measure = () => {
      const next =
        window.innerHeight - element.getBoundingClientRect().top - gap;
      setHeight((prev) =>
        prev !== undefined && Math.abs(prev - next) < 1 ? prev : next,
      );
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (element.parentElement) observer.observe(element.parentElement);
    const scrollParent = scrollParentOf(element);
    if (scrollParent) {
      for (const child of scrollParent.children) observer.observe(child);
    }

    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref, enabled, gap]);

  return enabled ? height : undefined;
}
